import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Gavel } from '@phosphor-icons/react';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useAuthStore } from '@/features/auth/store';

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function HearingAlert() {
  const [dismissed, setDismissed] = useState(false);
  const { hearings, fetchHearings } = useHearingsStore();
  const user = useAuthStore((s) => s.user);
  // /hearings is chambers-scoped and refuses an external solicitor. The portal shares this
  // shell, so without the guard every solicitor page fired a request that could only 403.
  // Their hearing information is served by the portal's own calendar route instead.
  const isExternal = user?.memberType === 'solicitor';

  useEffect(() => { if (!isExternal) fetchHearings(); }, [fetchHearings, isExternal]);

  if (dismissed || !user || isExternal) return null;

  const basePath =
    user.memberType === 'silk' || user.memberType === 'junior' || user.memberType === 'pupil'
      ? '/barrister'
      : user.memberType === 'senior_clerk' || user.memberType === 'junior_clerk'
      ? '/clerk'
      : '/admin';

  // Upcoming hearings in the next 60 days, scheduled/confirmed
  const upcoming = hearings
    .filter(h => {
      const days = daysUntil(h.date);
      return days >= 0 && days <= 60 && (h.status === 'scheduled' || h.status === 'confirmed');
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (upcoming.length === 0 || !upcoming[0]) return null;

  const next = upcoming[0];
  const days = daysUntil(next.date);

  // Urgency: today/tomorrow = red, ≤7 days = amber, otherwise blue
  const urgency =
    days <= 1 ? 'red' :
    days <= 7 ? 'amber' :
    'neutral';

  const styles = {
    red:     'border-red-200 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300',
    amber:   'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/40 dark:bg-amber-900/20 dark:text-amber-300',
    neutral: 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700/50 dark:bg-slate-800/60 dark:text-slate-300',
  }[urgency];

  const dotStyles = {
    red:     'bg-red-500 dark:bg-red-400',
    amber:   'bg-amber-500 dark:bg-amber-400',
    neutral: 'bg-slate-400 dark:bg-slate-500',
  }[urgency];

  const label =
    days === 0 ? 'Today' :
    days === 1 ? 'Tomorrow' :
    `In ${days} days`;

  return (
    <div className={`hidden md:flex items-center gap-3 border-b px-6 py-2 text-sm ${styles}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${dotStyles}`} />
      <Gavel size={14} weight="duotone" className="shrink-0 opacity-70" />
      <span className="font-medium">{label}:</span>
      <span className="truncate">
        {next.type.replace(/_/g, ' ')} —{' '}
        <span className="font-medium">{next.matterName || next.matterNumber}</span>
      </span>
      <span className="shrink-0 opacity-70">{formatDate(next.date)}</span>
      {next.court && (
        <span className="hidden shrink-0 opacity-60 lg:inline">· {next.court}</span>
      )}
      {upcoming.length > 1 && (
        <span className="shrink-0 opacity-60">+{upcoming.length - 1} more</span>
      )}
      <Link
        to={`${basePath}/hearings`}
        className="ml-auto shrink-0 text-xs underline underline-offset-2 opacity-70 hover:opacity-100"
      >
        View all
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded p-0.5 opacity-50 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
